import React from "react";
import { motion } from "framer-motion";
import { FaShieldAlt, FaBolt, FaUsers, FaTrophy } from "react-icons/fa";

export default function About() {
  const features = [
    {
      icon: <FaShieldAlt />,
      title: "Safe & Secure",
      description: "Verified drivers, GPS tracking, and 24/7 support",
      color: "linear-gradient(135deg, #E63946, #dc2626)",
    },
    {
      icon: <FaBolt />,
      title: "Lightning Fast",
      description: "Quick bookings with riders arriving in minutes",
      color: "linear-gradient(135deg, #FFC300, #f97316)",
    },
    {
      icon: <FaUsers />,
      title: "Trusted Community",
      description: "Join thousands of happy riders",
      color: "linear-gradient(135deg, #a855f7, #ec4899)",
    },
    {
      icon: <FaTrophy />,
      title: "Best Prices",
      description: "Affordable rides without compromise",
      color: "linear-gradient(135deg, #22c55e, #14b8a6)",
    },
  ];

  const width = typeof window !== "undefined" ? window.innerWidth : 1024;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="about"
      style={{
        padding: width < 768 ? "60px 16px" : "96px 20px",
        background: "white",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
          style={{
            textAlign: "center",
            marginBottom: width < 768 ? "40px" : "64px",
          }}
        >
          <span
            style={{
              background: "linear-gradient(to right, #E63946, #FFC300)",
              color: "white",
              padding: width < 768 ? "6px 18px" : "8px 24px",
              borderRadius: "50px",
              fontSize: width < 768 ? "16px" : "20px",
              fontWeight: "600",
            }}
          >
            About INRYDE
          </span>
          <h2
            style={{
              fontSize: width < 768 ? "clamp(24px, 6vw, 36px)" : "clamp(32px, 6vw, 48px)",
              fontWeight: "bold",
              color: "#1C1C1C",
              margin: "24px 0",
            }}
          >
            Why Choose{" "}
            <span
              style={{
                background: "linear-gradient(to right, #E63946, #FFC300)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              INRYDE
            </span>
            ?
          </h2>
          <p
            style={{
              fontSize: width < 768 ? "16px" : "20px",
              color: "#6C757D",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            We're revolutionizing urban mobility with safe, fast, and affordable rides across India
          </p>
        </motion.div>

        {/* Feature cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          style={{
            display: "grid",
            gridTemplateColumns: width < 500 ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
            gap: width < 768 ? "20px" : "32px",
          }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                scale: 1.04,
                boxShadow: "0 20px 45px rgba(230, 57, 70, 0.2)",
                transition: { duration: 0.3 },
              }}
              style={{
                position: "relative",
                background: "white",
                borderRadius: "20px",
                padding: width < 768 ? "24px" : "36px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                border: "1px solid #f1f1f1",
                transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-12px) scale(1.03)";
                e.currentTarget.style.boxShadow = "0 20px 45px rgba(230, 57, 70, 0.25)";
                e.currentTarget.style.border = "1px solid rgba(230, 57, 70, 0.3)";
                const icon = e.currentTarget.querySelector(".icon-box");
                if (icon) {
                  icon.style.transform = "scale(1.15)";
                  icon.style.boxShadow = "0 0 25px rgba(230, 57, 70, 0.5)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
                e.currentTarget.style.border = "1px solid #f1f1f1";
                const icon = e.currentTarget.querySelector(".icon-box");
                if (icon) {
                  icon.style.transform = "scale(1)";
                  icon.style.boxShadow = "0 10px 20px rgba(0,0,0,0.2)";
                }
              }}
            >
              {/* Gradient blur glow behind */}
              <div
                style={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: feature.color,
                  opacity: 0.05,
                  zIndex: 0,
                  transition: "opacity 0.3s ease",
                }}
              ></div>

              <div
                className="icon-box"
                style={{
                  width: "64px",
                  height: "64px",
                  background: feature.color,
                  borderRadius: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "30px",
                  color: "white",
                  marginBottom: "24px",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  zIndex: 1,
                }}
              >
                {feature.icon}
              </div>

              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#1C1C1C",
                  marginBottom: "12px",
                  zIndex: 1,
                  position: "relative",
                }}
              >
                {feature.title}
              </h3>
              <p
                style={{
                  color: "#6C757D",
                  lineHeight: "1.6",
                  position: "relative",
                  zIndex: 1,
                  fontSize: width < 768 ? "14px" : "16px",
                }}
              >
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          style={{
            marginTop: "80px",
            background: "linear-gradient(to right, #E63946, #FFC300)",
            borderRadius: "24px",
            padding: width < 768 ? "32px" : "48px",
            color: "white",
            boxShadow: "0 20px 40px rgba(230, 57, 70, 0.3)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: width < 500 ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: width < 768 ? "20px" : "32px",
              textAlign: "center",
            }}
          >
            <motion.div whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}>
              <div
                style={{
                  fontSize: width < 768 ? "36px" : "48px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                99.9%
              </div>
              <div style={{ opacity: 0.9 }}>Satisfaction Rate</div>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}>
              <div
                style={{
                  fontSize: width < 768 ? "36px" : "48px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                24/7
              </div>
              <div style={{ opacity: 0.9 }}>Customer Support</div>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}>
              <div
                style={{
                  fontSize: width < 768 ? "36px" : "48px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                &lt;5 Min
              </div>
              <div style={{ opacity: 0.9 }}>Average Wait Time</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
